import { useState, useMemo } from 'react'
import { FileText, TrendingUp, Wallet } from 'lucide-react'
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Line, ComposedChart, ResponsiveContainer, LabelList,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// ── Tipos ──────────────────────────────────────────────────────────────────

interface FunnelMes1 {
  registros_mes1_por_rep: number
  tasa_participacion_mes1: number
  activos_mes1_por_rep: number
  facturas_mes1_por_rep: number
  fact_por_dep_activo: number
  puntos_por_factura: number
  puntos_mes1_por_rep: number
  potencial_sol_por_rep: number
  tasa_canje_mensual: number
  canje_sol_por_rep: number
  canje_usd_por_rep: number
  n_registros_mes1: number
  n_activos_mes1: number
  n_facturas_mes1: number
  n_puntos_mes1: number
  n_potencial_sol: number
  n_canje_sol: number
  n_canje_usd: number
}

interface CurvaPoint {
  mes: string
  registros: number
  acumulado: number
}

interface ProyeccionData {
  representantes_base: number
  promedios: {
    farmacias_por_rep: number
    registros_mes1_por_rep: number
    facturas_mes1_por_rep: number
    canje_ref_usd_por_rep: number
  }
  proyeccion: {
    dependientes_totales: number
    registros_mes1: number
    facturas_mes1: number
    canje_sol_mes1: number
  }
  curva_registros: CurvaPoint[]
  funnel_mes1: FunnelMes1
  nota_metodologia: string
}

// ── Constantes ──────────────────────────────────────────────────────────────

const BLUE = '#6a5cbc'
const BLUE_LIGHT = '#6a5cbc18'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('es-PE')
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon,
}: {
  label: string; value: string; sub?: string; icon: React.ElementType
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${BLUE_LIGHT}, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <Icon className="h-9 w-9" style={{ color: BLUE }} />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-3xl font-bold text-foreground leading-tight">{value}</div>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Flecha del funnel ─────────────────────────────────────────────────────────

function Arrow() {
  return (
    <div className="flex items-center justify-center py-1">
      <div className="w-px h-4 bg-gray-300" />
      <div className="absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-300 mt-5" />
    </div>
  )
}

// Paso del funnel
function FunnelStep({
  step, label, valuePrimary, valueSub, note,
}: {
  step: number; label: string; valuePrimary: string; valueSub?: string; note?: string
}) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5"
        style={{ backgroundColor: BLUE }}
      >
        {step}
      </div>
      <div className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-card px-4 py-3"
        style={{ backgroundImage: `linear-gradient(to right, ${BLUE_LIGHT}, transparent 50%)` }}
      >
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-xl font-bold text-foreground">{valuePrimary}</p>
        {valueSub && <p className="text-xs text-muted-foreground mt-0.5">{valueSub}</p>}
        {note && <p className="text-xs text-muted-foreground/60 mt-1 italic">{note}</p>}
      </div>
    </div>
  )
}

// ── Tooltip custom ────────────────────────────────────────────────────────────

function CurvaTooltip({ active, payload, label }: {
  active?: boolean; payload?: { value: number; name: string }[]; label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-muted-foreground">
          {p.name === 'registros' ? 'Nuevos' : 'Acumulado'}: <span className="font-medium text-foreground">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────

export function MediafarmaPeruProyeccionPage() {
  useDocumentTitle('Proyección Fidelización · Medifarma Peru')

  const { data, isLoading, isError } = useDashReport<ProyeccionData>('medifarma-peru/proyeccion')
  const [reps, setReps] = useState<number>(277)

  // Recalcular proyección y curva en cliente al cambiar reps
  const { proyeccion, curva } = useMemo(() => {
    if (!data) return { proyeccion: null, curva: [] }
    const p = data.promedios
    const dependientes = Math.round(reps * p.farmacias_por_rep)
    const mes1 = Math.round(reps * p.registros_mes1_por_rep)
    const resto = dependientes - mes1
    // Misma distribución que el backend (Medifarma pattern)
    const dist = [296, 149, 173, 75]
    const distTotal = dist.reduce((a, b) => a + b, 0)
    let acum = mes1
    const curvaCalc: CurvaPoint[] = [
      { mes: 'Mes 1', registros: mes1, acumulado: mes1 },
      ...dist.map((d, i) => {
        const reg = Math.round(resto * d / distTotal)
        acum += reg
        return { mes: `Mes ${i + 2}`, registros: reg, acumulado: acum }
      }),
    ]
    const f = data.funnel_mes1
    return {
      proyeccion: {
        dependientes_totales: dependientes,
        registros_mes1: mes1,
        facturas_mes1: Math.round(reps * p.facturas_mes1_por_rep),
        canje_sol_mes1: Math.round(reps * f.canje_sol_por_rep),
        canje_usd_mes1: Math.round(reps * f.canje_usd_por_rep),
      },
      curva: curvaCalc,
    }
  }, [data, reps])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BLUE }} />
      </div>
    )
  }

  if (isError || !data || !proyeccion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Error al cargar los datos.
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Proyección — Programa de Fidelización
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Estimación basada en benchmarks de programas de fidelización · Perú
                </p>
              </div>
            </div>
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxC1wKpIiaTpYQ9UIOCvWwoUZDLGpSxWvaR5x1cEGXyYaSxkCDU6SkRHDZ&s=10"
              alt="Medifarma Peru"
              className="h-12 w-auto flex-shrink-0 object-contain"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Resumen ejecutivo ── */}
      <Card className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-card">
        <CardContent className="py-4 px-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            En el <span className="font-semibold text-foreground">primer mes</span>, con{' '}
            <span className="font-semibold text-foreground">{fmt(reps)} visitadores</span> se esperan{' '}
            <span className="font-semibold text-foreground">{fmt(proyeccion.registros_mes1)} dependientes de farmacia</span> registrados,
            quienes subirán aproximadamente{' '}
            <span className="font-semibold text-foreground">{fmt(proyeccion.facturas_mes1)} facturas</span>.
            Los puntos generados representan un pasivo de <span className="font-semibold text-foreground">S/ {fmt(proyeccion.canje_sol_mes1)}</span> en canjes estimados
            {' '}<span className="text-xs">(~${fmt(proyeccion.canje_usd_mes1)} · tasa histórica 55%)</span>.
            Los canjes se materializan a partir del mes 2. Los meses siguientes se calcularán con los datos reales del mes 1.
          </p>
        </CardContent>
      </Card>

      {/* ── Input + KPIs ── */}
      <div className="space-y-3">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-card px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Representantes</span>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={2000}
              value={reps}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                if (!isNaN(v) && v > 0) setReps(v)
              }}
              className="w-24 text-center text-xl font-bold border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 text-foreground bg-background"
              style={{ borderColor: BLUE }}
            />
            <span className="text-sm text-muted-foreground">representantes</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            icon={TrendingUp}
            label="Dependientes registrados — mes 1"
            value={fmt(proyeccion.registros_mes1)}
            sub={`${fmt(reps)} visitadores × ${data.promedios.registros_mes1_por_rep} dep./visitador`}
          />
          <KpiCard
            icon={FileText}
            label="Facturas subidas — mes 1"
            value={fmt(proyeccion.facturas_mes1)}
            sub={`${data.promedios.facturas_mes1_por_rep} facturas/visitador · mes 1`}
          />
          <KpiCard
            icon={Wallet}
            label="Costo canjes estimado (acumulado)"
            value={`S/ ${fmt(proyeccion.canje_sol_mes1)}`}
            sub={`~$${fmt(proyeccion.canje_usd_mes1)} · 1 punto = S/ 1.00 · tasa histórica 55%`}
          />
        </div>
      </div>

      {/* ── Curva de registros ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Ramp-up de dependientes registrados — {fmt(reps)} representantes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={curva} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bar" tick={{ fontSize: 11 }} width={55} tickFormatter={(v) => fmt(v)} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<CurvaTooltip />} />
              <Bar yAxisId="bar" dataKey="registros" name="registros" fill={BLUE} radius={[4, 4, 0, 0]} opacity={0.85}>
                <LabelList dataKey="registros" position="top" formatter={(v: number) => fmt(v)} style={{ fontSize: 11, fill: '#6b7280' }} />
              </Bar>
              <Line yAxisId="line" dataKey="acumulado" name="acumulado" stroke="#2fbc80" strokeWidth={2} dot={{ r: 4, fill: '#2fbc80' }} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-6 mt-2 justify-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: BLUE }} />
              Registros nuevos por mes
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-4 h-0.5 bg-[#2fbc80]" />
              Acumulado
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Funnel mes 1 ── */}
      {(() => {
        const f = data.funnel_mes1
        const n = reps
        return (
          <Card className="border border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Cómo se construye la proyección — mes 1
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Por dependiente */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Por dependiente (promedio benchmark)
                  </p>
                  {(() => {
                    const puntosDepActivo = Math.round(f.fact_por_dep_activo * f.puntos_por_factura)
                    const canjeDepActivo = Math.round(puntosDepActivo * f.tasa_canje_mensual)
                    const canjeUsdDep = (canjeDepActivo * 0.30).toFixed(1)
                    return (
                      <div className="space-y-2">
                        <FunnelStep
                          step={1}
                          label="Dependientes registrados — mes 1"
                          valuePrimary={`${f.registros_mes1_por_rep} dep. por representante`}
                          valueSub={`Cada representante registra ~${f.registros_mes1_por_rep} dependientes de farmacia`}
                        />
                        <Arrow />
                        <FunnelStep
                          step={2}
                          label="Participación — sube ≥1 factura"
                          valuePrimary={`${Math.round(f.tasa_participacion_mes1 * 100)}% participa en mes 1`}
                          valueSub={`1 de cada ${Math.round(1 / f.tasa_participacion_mes1)} dependientes es activo · ~${f.activos_mes1_por_rep} por rep.`}
                        />
                        <Arrow />
                        <FunnelStep
                          step={3}
                          label="Facturas si activo — mes 1"
                          valuePrimary={`~${f.fact_por_dep_activo} facturas`}
                        />
                        <Arrow />
                        <FunnelStep
                          step={4}
                          label="Puntos si activo — mes 1"
                          valuePrimary={`~${puntosDepActivo} puntos`}
                          valueSub={`${f.puntos_por_factura} puntos por factura · 1 punto = S/ 1.00`}
                        />
                        <Arrow />
                        <FunnelStep
                          step={5}
                          label="Canje estimado si activo"
                          valuePrimary={`S/ ${canjeDepActivo} / dep. activo`}
                          valueSub={`~$${canjeUsdDep} · tasa histórica ${Math.round(f.tasa_canje_mensual * 100)}%`}
                          note="Los canjes se materializan a partir del mes 2"
                        />
                      </div>
                    )
                  })()}
                </div>

                {/* Para N representantes */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Para {fmt(n)} visitadores — mes 1
                  </p>
                  <div className="space-y-2">
                    <FunnelStep
                      step={1}
                      label="Dependientes registrados — mes 1"
                      valuePrimary={`${fmt(Math.round(n * f.registros_mes1_por_rep))} dependientes`}
                    />
                    <Arrow />
                    <FunnelStep
                      step={2}
                      label="Dependientes activos"
                      valuePrimary={`${fmt(Math.round(n * f.activos_mes1_por_rep))} dep. activos`}
                      valueSub={`${Math.round(f.tasa_participacion_mes1 * 100)}% de los registrados`}
                    />
                    <Arrow />
                    <FunnelStep
                      step={3}
                      label="Facturas subidas — mes 1"
                      valuePrimary={`${fmt(Math.round(n * f.facturas_mes1_por_rep))} facturas`}
                    />
                    <Arrow />
                    <FunnelStep
                      step={4}
                      label="Puntos generados — mes 1"
                      valuePrimary={`${fmt(Math.round(n * f.puntos_mes1_por_rep))} puntos`}
                      valueSub={`S/ ${fmt(Math.round(n * f.potencial_sol_por_rep))} potencial de canje`}
                    />
                    <Arrow />
                    <FunnelStep
                      step={5}
                      label="Costo de canjes estimado (acumulado)"
                      valuePrimary={`S/ ${fmt(Math.round(n * f.canje_sol_por_rep))}`}
                      valueSub={`~$${fmt(Math.round(n * f.canje_usd_por_rep))} · tasa histórica ${Math.round(f.tasa_canje_mensual * 100)}%`}
                    />
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        )
      })()}

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground/40 pb-2">
        Tonal Strategies · Proyección{' '}
        {new Date().toLocaleDateString('es-PE', { year: 'numeric', month: 'long' })}
      </p>
    </div>
  )
}
